import { IPeriod, TPeriodUnit, TSubGroupType } from '@geovelo-frontends/commons';

// Query keys must be built from primitives only (ids, ISO date strings) — never pass IPeriod's
// Moment instances or model objects (Geogroup, Partner, Event) directly, since TanStack Query's
// key hashing needs a value that serializes predictably to dedupe two call sites correctly.
function serializePeriod(period: IPeriod<TPeriodUnit | 'all_period'>) {
  return { from: period.from.toISOString(), to: period.to.toISOString(), unit: period.unit };
}

// Every entity below is structured as { all(...rootParams), detail(...rootParams, ...extraParams) }
// where `detail`'s key always starts with exactly `all`'s key — TanStack Query matches queryKeys
// by prefix, so `invalidateQueries({ queryKey: queryKeys.<entity>.all(id) })` after a mutation
// invalidates every `detail` variant for that entity/id in one call, regardless of which extra
// filters (period, subgroup, statuses, ...) each variant was fetched with. See AGENTS.md
// ("TanStack Query — queries, mutations, invalidation") for the full write-up of this convention.
export const queryKeys = {
  activityStats: {
    all: (geoGroupId: number | undefined) => ['activity-stats', geoGroupId] as const,
    detail: (geoGroupId: number | undefined, period: IPeriod<TPeriodUnit | 'all_period'>) =>
      ['activity-stats', geoGroupId, serializePeriod(period)] as const,
  },
  geogroupStats: {
    all: (geoGroupId: number | undefined) => ['geogroup-stats', geoGroupId] as const,
    detail: (geoGroupId: number | undefined, period: IPeriod<TPeriodUnit | 'all_period'>) =>
      ['geogroup-stats', geoGroupId, serializePeriod(period)] as const,
  },
  userTracesStats: {
    all: (employeeId: number | undefined) => ['user-traces-stats', employeeId] as const,
    detail: (employeeId: number | undefined, period: IPeriod<TPeriodUnit | 'all_period'>) =>
      ['user-traces-stats', employeeId, serializePeriod(period)] as const,
  },
  challengeLeaderboard: {
    all: (challengeId: number | undefined) => ['challenge-leaderboard', challengeId] as const,
    detail: (challengeId: number | undefined, subgroupId: number | undefined) =>
      ['challenge-leaderboard', challengeId, subgroupId] as const,
  },
  transportHabits: {
    all: (partnerId: number | undefined) => ['transport-habits', partnerId] as const,
    detail: (partnerId: number | undefined, subGroupId: number | undefined) =>
      ['transport-habits', partnerId, subGroupId] as const,
  },
  event: {
    all: (eventId: number | undefined) => ['event', eventId] as const,
  },
  eventRanking: {
    all: (eventId: number) => ['event-ranking', eventId] as const,
    detail: (eventId: number, ownGeoGroupId: number | undefined) =>
      ['event-ranking', eventId, ownGeoGroupId] as const,
  },
  eventRankingWindow: {
    all: (eventId: number) => ['event-ranking-window', eventId] as const,
    detail: (eventId: number, rank: number | undefined) =>
      ['event-ranking-window', eventId, rank] as const,
  },
  // Every employees list/lookup for a partner, whatever the filter (statuses, groupsIds) — a
  // mutation that adds/removes/changes an employee's status (accept, reject, import, invite,
  // update, delete...) should invalidate `employees.all(partnerId)`, not one specific `.list`/
  // `.waitingForApproval` variant, since it can't know every filtered view currently mounted.
  employees: {
    all: (partnerId: number | undefined) => ['employees', partnerId] as const,
    list: (partnerId: number | undefined, statuses: string[], groupsIds: number[] | undefined) =>
      ['employees', partnerId, 'list', statuses, groupsIds] as const,
    waitingForApproval: (partnerId: number | undefined, groupsIds: number[] | undefined) =>
      ['employees', partnerId, 'waiting-for-approval', groupsIds] as const,
    // Partner-wide counts + full "waiting for approval" list (paginated to completion). Distinct
    // from `waitingForApproval` above (that one is event/site-scoped and capped at one page of 50;
    // this one is partner-wide and complete).
    summary: (partnerId: number | undefined) => ['employees', partnerId, 'summary'] as const,
    // A single employee's own detail (e.g. the FMD employee detail page) — still under the
    // `employees` prefix so `useUpdateEmployeeMutation`'s existing invalidation covers it too.
    detail: (partnerId: number | undefined, employeeId: number | undefined) =>
      ['employees', partnerId, 'detail', employeeId] as const,
  },
  potentialCyclists: {
    all: (partnerId: number | undefined) => ['potential-cyclists', partnerId] as const,
    detail: (partnerId: number | undefined, siteId: number | undefined) =>
      ['potential-cyclists', partnerId, siteId] as const,
  },
  mobilitySurveyStats: {
    all: (partnerId: number | undefined) => ['mobility-survey-stats', partnerId] as const,
    detail: (partnerId: number | undefined, siteId: number | undefined) =>
      ['mobility-survey-stats', partnerId, siteId] as const,
  },
  // Global, session-independent (same for every user/partner) — no `detail` variant needed.
  stripeProducts: {
    all: () => ['stripe-products'] as const,
  },
  // Keyed by partnerId (not geoGroupId) for `all` so invalidating after a mutation (activating an
  // automatic/editorialized challenge) covers the entry regardless of which geogroup it was
  // fetched for.
  partnerAvailableEvents: {
    all: (partnerId: number | undefined) => ['partner-available-events', partnerId] as const,
    detail: (partnerId: number | undefined, geoGroupId: number | undefined) =>
      ['partner-available-events', partnerId, geoGroupId] as const,
  },
  partnerInvitationCode: {
    all: (partnerId: number | undefined) => ['partner-invitation-code', partnerId] as const,
  },
  partnerMobilitySurvey: {
    all: (partnerId: number | undefined) => ['partner-mobility-survey', partnerId] as const,
  },
  partnerSubgroups: {
    all: (partnerId: number | undefined) => ['partner-subgroups', partnerId] as const,
  },
  partnerFMDConfig: {
    all: (partnerId: number | undefined) => ['partner-fmd-config', partnerId] as const,
  },
  partnerFreeFMDInfoCount: {
    all: (partnerId: number | undefined) => ['partner-free-fmd-info-count', partnerId] as const,
  },
  partnerGeogroup: {
    all: (partnerId: number | undefined) => ['partner-geogroup', partnerId] as const,
  },
  highlightedEvents: {
    all: (
      partnerId: number | undefined,
      geoGroupId: number | undefined,
      onlyBannerEnabled: boolean,
      includeFinished: boolean,
    ) => ['highlighted-events', partnerId, geoGroupId, onlyBannerEnabled, includeFinished] as const,
  },
  highlightedEventChallenges: {
    all: (geoGroupId: number | undefined) => ['highlighted-event-challenges', geoGroupId] as const,
  },
  eventChallenge: {
    detail: (geoGroupId: number | undefined, eventId: number | undefined) =>
      ['event-challenge', geoGroupId, eventId] as const,
  },
  subgroupsLeaderboard: {
    all: (partnerId: number | undefined, subGroupDisplay: string) =>
      ['subgroups-leaderboard', partnerId, subGroupDisplay] as const,
  },
  eventOffers: {
    all: (eventId: number | undefined) => ['event-offers', eventId] as const,
  },
  eventCoverage: {
    root: ['event-coverage'] as const,
    all: (
      userId: number | undefined,
      eventId: number | undefined,
      geoGroupId: number | undefined,
    ) => ['event-coverage', userId, eventId, geoGroupId] as const,
  },
  fmdHomeSummary: {
    all: (partnerId: number | undefined) => ['fmd-home-summary', partnerId] as const,
    detail: (
      partnerId: number | undefined,
      fmdConfigType: string | undefined,
      annualPeriodStartMonth: number | undefined,
      annualPeriodStartDay: number | undefined,
    ) =>
      [
        'fmd-home-summary',
        partnerId,
        fmdConfigType,
        annualPeriodStartMonth,
        annualPeriodStartDay,
      ] as const,
  },
  challenge: {
    all: (geoGroupId: number | undefined) => ['challenge', geoGroupId] as const,
    detail: (geoGroupId: number | undefined, id: number | undefined) =>
      ['challenge', geoGroupId, id] as const,
  },
  userChallenge: {
    all: (geoGroupId: number | undefined) => ['user-challenge', geoGroupId] as const,
    detail: (geoGroupId: number | undefined, challengeId: number | undefined) =>
      ['user-challenge', geoGroupId, challengeId] as const,
  },
  ownChallengeRank: {
    all: (challengeId: number | undefined) => ['own-challenge-rank', challengeId] as const,
    detail: (
      challengeId: number | undefined,
      subgroupId: number | undefined,
      userId: number | undefined,
    ) => ['own-challenge-rank', challengeId, subgroupId, userId] as const,
  },
  employeeCommutingTripOccurrencesCount: {
    detail: (employeeId: number | undefined, dateStart: string, dateEnd: string) =>
      ['employee-commuting-trip-occurrences-count', employeeId, dateStart, dateEnd] as const,
  },
  // `future`/pageSize:100 is deliberately shared between the challenges preview (sliced to 3
  // client-side) and the dedicated future-challenges tab — same request either way, and fetching
  // the full 100 once means the preview never needs its own separate 3-item request.
  challenges: {
    all: (geoGroupId: number | undefined) => ['challenges', geoGroupId] as const,
    detail: (geoGroupId: number | undefined, state: string[], pageSize: number, order: string) =>
      ['challenges', geoGroupId, state, pageSize, order] as const,
  },
  userChallengesBatch: {
    all: (geoGroupId: number | undefined) => ['user-challenges-batch', geoGroupId] as const,
    detail: (geoGroupId: number | undefined, challengeIds: number[]) =>
      ['user-challenges-batch', geoGroupId, challengeIds] as const,
  },
  // User-selectable period (month/year), unlike fmdHomeSummary which is always "current period".
  fmdPeriodStats: {
    all: (partnerId: number | undefined) => ['fmd-period-stats', partnerId] as const,
    detail: (partnerId: number | undefined, startDate: string, endDate: string) =>
      ['fmd-period-stats', partnerId, startDate, endDate] as const,
  },
  // geogroupId undefined means "whole company" (not scoped to a site) — a distinct cache entry
  // from any specific site's id, shared between the single-site legend panel and the
  // all-sites overview panel (same underlying per-site fetch, different presentation).
  homeWorkDistances: {
    all: (partnerId: number | undefined) => ['home-work-distances', partnerId] as const,
    detail: (partnerId: number | undefined, geogroupId: number | undefined) =>
      ['home-work-distances', partnerId, geogroupId] as const,
  },
  // Holding partners only — the list of communities (member companies) aggregated under the
  // holding's own geogroup.
  aggregatedGroups: {
    all: (partnerId: number | undefined) => ['aggregated-groups', partnerId] as const,
  },
  // Shared between the stats page's preview card (limit 3, fixed ordering) and the "see all" page
  // (no limit / free-tier limit, toggleable ordering) — different params so not literally the same
  // cache entry, but one implementation instead of two hand-rolled fetch+zero-fill copies.
  subgroupsStats: {
    all: (geoGroupId: number | undefined) => ['subgroups-stats', geoGroupId] as const,
    detail: (
      geoGroupId: number | undefined,
      subgroupType: TSubGroupType,
      period: IPeriod<TPeriodUnit | 'all_period'>,
      limit: number | undefined,
      ordering: string,
    ) =>
      [
        'subgroups-stats',
        geoGroupId,
        subgroupType,
        serializePeriod(period),
        limit,
        ordering,
      ] as const,
  },
  averageJourneyStats: {
    all: (geoGroupId: number | undefined) => ['average-journey-stats', geoGroupId] as const,
    detail: (geoGroupId: number | undefined, period: IPeriod<TPeriodUnit | 'all_period'>) =>
      ['average-journey-stats', geoGroupId, serializePeriod(period)] as const,
  },
  // Home page's incitation card — raw (no auto-compute-and-save fallback), unlike
  // potentialCyclists below, which the incitation flow's own pages rely on for that fallback.
  homeIncitationStats: {
    all: (partnerId: number | undefined) => ['home-incitation-stats', partnerId] as const,
  },
  jerseysHolders: {
    all: (geoGroupId: number | undefined) => ['jerseys-holders', geoGroupId] as const,
  },
  // Site-scoped (unlike homeIncitationStats, which is always company-wide) — used by the pilot
  // tab's active-cyclists card.
  activeCommuterStats: {
    all: (partnerId: number | undefined) => ['active-commuter-stats', partnerId] as const,
    detail: (partnerId: number | undefined, groupId: number | undefined) =>
      ['active-commuter-stats', partnerId, groupId] as const,
  },
  employeesStats: {
    all: (partnerId: number | undefined) => ['employees-stats', partnerId] as const,
  },
  // Shared between pages/cyclability (standalone page) and pages/incitation's Accessibility tab —
  // both render the same `Isochrones` component for the same site, previously with their own
  // local (non-shared) fetch each.
  isochrones: {
    all: (partnerId: number | undefined) => ['isochrones', partnerId] as const,
    detail: (partnerId: number | undefined, siteId: number | undefined) =>
      ['isochrones', partnerId, siteId] as const,
  },
  individualJerseysLeaderboard: {
    all: (geoGroupId: number | undefined) =>
      ['individual-jerseys-leaderboard', geoGroupId] as const,
    detail: (
      geoGroupId: number | undefined,
      allJerseysEnabled: boolean,
      currentUserId: number | undefined,
    ) => ['individual-jerseys-leaderboard', geoGroupId, allJerseysEnabled, currentUserId] as const,
  },
};
